/*
  Kiwi Ears · wishlist (Rene 2026-08-23).

  A localStorage wishlist — no app, no account, no server state. The store is
  a JSON array of product HANDLES under 'kiwi:wishlist'. Handles, not ids,
  because the page renders by fetching /products/<handle>.js, and a handle in
  the URL is what survives product retitles.

  Three jobs:
    1. TOGGLES. Any [data-wishlist-toggle] with data-handle becomes a heart:
       click flips membership, aria-pressed carries the state (and the CSS
       styles from it, so a screen reader and the visuals cannot disagree).
       Delegated from the document, so cards injected later still work.
    2. THE BADGE. Every [data-wishlist-count] shows the count, hidden at
       zero — same visual grammar as the header's cart bubble.
    3. THE PAGE. [data-wishlist-page] renders the saved products by fetching
       /products/<handle>?section_id=wl-card — Shopify's Section Rendering
       API, which returns the real house card rendered by Liquid. This used
       to build the card markup here in a template string, and that is
       exactly why the wishlist ended up with a card style found nowhere
       else on the site: JS cannot read metafields, so the rating and the
       collaboration line were simply unavailable and the card had to be
       reinvented smaller. Rendering server-side means one card definition
       (snippets/pk-pair-card) for the whole store. A product delisted
       since it was saved is PRUNED rather than rendered as an error — a
       wishlist that scolds you about dead products is worse than one that
       quietly lets them go. Note that a dead handle does NOT come back as
       a 404 here: the Section Rendering API answers 200 with an empty
       section wrapper, so the test is whether a card came back, not what
       the status line said.

  Storage can throw (private mode, blocked site data) — every touch is
  wrapped, and the feature degrades to "hearts do nothing" rather than
  breaking the page around it.
*/
(function () {
  if (window.__kiwiWishlistReady) return;
  window.__kiwiWishlistReady = true;

  var KEY = 'kiwi:wishlist';

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    paintBadges();
    document.dispatchEvent(new CustomEvent('kiwi:wishlist-change', { detail: { count: list.length } }));
  }
  function has(h) { return read().indexOf(h) !== -1; }
  function toggle(h) {
    var list = read();
    var i = list.indexOf(h);
    if (i === -1) list.unshift(h);
    else list.splice(i, 1);
    write(list);
    return i === -1;
  }
  function remove(h) {
    var list = read();
    var i = list.indexOf(h);
    if (i !== -1) { list.splice(i, 1); write(list); }
  }

  window.KiwiWishlist = { list: read, has: has, toggle: toggle, remove: remove };

  /* ------------------------------------------------------------ badges */
  function paintBadges() {
    var n = read().length;
    [].slice.call(document.querySelectorAll('[data-wishlist-count]')).forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  /* ----------------------------------------------------------- toggles */
  function paintToggles(root) {
    [].slice.call((root || document).querySelectorAll('[data-wishlist-toggle]')).forEach(function (b) {
      var on = has(b.getAttribute('data-handle'));
      b.setAttribute('aria-pressed', String(on));
      var label = on ? (b.getAttribute('data-label-on') || 'Remove from wishlist')
                     : (b.getAttribute('data-label-off') || 'Add to wishlist');
      b.setAttribute('aria-label', label);
    });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-wishlist-toggle]');
    if (!b) return;
    e.preventDefault();
    var h = b.getAttribute('data-handle');
    if (!h) return;
    toggle(h);
    paintToggles();
    var live = document.querySelector('[data-wishlist-live]');
    if (live) live.textContent = (has(h) ? 'Added to' : 'Removed from') + ' your wishlist.';
  });

  /* ------------------------------------------------------------ page */

  /* One fetch per saved handle, same as before — but the response is rendered
     card markup rather than product JSON, so nothing about how a card looks is
     decided here. The slots are created up front in wishlist order and filled
     in place: fetches finish out of order, and appending on arrival used to
     shuffle the grid on every reload. */
  function renderPage() {
    var mount = document.querySelector('[data-wishlist-page]');
    if (!mount) return;
    var empty = document.querySelector('[data-wishlist-empty]');
    var handles = read();

    function showEmpty() {
      mount.hidden = true;
      if (empty) empty.hidden = false;
    }

    if (!handles.length) { showEmpty(); return; }
    mount.hidden = false;
    if (empty) empty.hidden = true;
    mount.textContent = '';

    var pending = handles.length;
    var kept = 0;

    handles.forEach(function (h) {
      var li = document.createElement('li');
      li.setAttribute('data-wl-slot', h);
      mount.appendChild(li);

      fetch('/products/' + encodeURIComponent(h) + '?section_id=wl-card')
        .then(function (r) {
          if (!r.ok) throw new Error('gone');
          return r.text();
        })
        .then(function (html) {
          /* The Section Rendering API wraps the section in its own shopify-section
             div. Take the card out of it rather than nesting the wrapper inside
             the grid, where it would become the grid item and the card would
             lose its cell. */
          var host = document.createElement('div');
          host.innerHTML = html;
          var item = host.querySelector('.wl-item');
          if (!item) throw new Error('empty');
          li.appendChild(item);
          kept++;
          /* the lift is wired at load; these cards arrive after it */
          document.dispatchEvent(new CustomEvent('kiwi:cards-added'));
        })
        .catch(function () {
          li.remove();
          remove(h);
        })
        .finally(function () {
          pending--;
          if (pending === 0 && kept === 0) showEmpty();
        });
    });
  }

  /* Quick-add from a wishlist card is handled by assets/kiwi-quickadd.js
     (Rene 2026-09-04). This file used to carry its own copy, scoped to
     [data-wishlist-page] precisely because each section bound its own handler
     and an unscoped listener here would have added twice. There is now exactly
     one delegated handler for .pk-padd site-wide, so the copy is gone and the
     scoping problem with it. */

  /* remove from the page — the card leaves, and when the last one goes the
     empty state comes back without a reload */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-remove-handle]');
    if (!b) return;
    remove(b.getAttribute('data-remove-handle'));
    var li = b.closest('[data-wl-slot]');
    if (li) li.remove();
    paintToggles();
    var mount = document.querySelector('[data-wishlist-page]');
    var empty = document.querySelector('[data-wishlist-empty]');
    if (mount && !mount.children.length) {
      mount.hidden = true;
      if (empty) empty.hidden = false;
    }
    var live = document.querySelector('[data-wishlist-live]');
    if (live) live.textContent = 'Removed from your wishlist.';
  });

  function arm() {
    paintBadges();
    paintToggles();
    renderPage();
  }
  if (document.readyState !== 'loading') arm();
  else document.addEventListener('DOMContentLoaded', arm);
})();
